/**
 * DETAIL-7C.3 / 7C.4 — Global header Saved Items.
 *
 * One store: window.DiamondSaved / ivanovSavedDiamonds.
 * Preview uses save-time snapshots only. Does not hydrate, compare,
 * or create a second purchase flow. V1 preview content is diamonds.
 */
(function (global) {
  "use strict";

  if (global.DiamondHeaderSaved && global.DiamondHeaderSaved.PREVIEW_LIMIT === 4) {
    return;
  }

  var PREVIEW_LIMIT = 4;
  var SEARCH_PATH = "/pages/diamond-search";
  var DETAIL_PATH = "/pages/diamond-details";
  var PAGE_PATH = "/pages/saved-items";
  var DESKTOP_MEDIA = "(min-width: 990px)";
  var CHANGE_EVENT = "ivanov:saved-diamonds-change";
  var CUT_LABELS = {
    ID: "Ideal",
    EX: "Excellent",
    VG: "Very Good",
    GD: "Good",
    FR: "Fair",
  };
  var FALLBACK_SVG =
    '<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M16 24 L32 8 L48 24 L32 58 Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
    '<path d="M16 24 L48 24 M25 24 L32 58 M39 24 L32 58 M22 24 L32 32 L42 24" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round"/>' +
    "</svg>";

  function escapeHtml(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function isMeaningful(value) {
    var normalized = String(value == null ? "" : value).trim();
    if (!normalized) return false;
    var upper = normalized.toUpperCase();
    return upper !== "NONE" && upper !== "N/A" && upper !== "NA" && upper !== "-" && upper !== "\u2014";
  }

  function toTitleCase(value) {
    return String(value)
      .toLowerCase()
      .replace(/\b\w/g, function (c) {
        return c.toUpperCase();
      });
  }

  function savedApi() {
    return global.DiamondSaved || null;
  }

  function detailApi() {
    return global.DiamondDetail || null;
  }

  function formatCarat(carat) {
    var Detail = detailApi();
    if (Detail && typeof Detail.formatCarat === "function") {
      var fromDetail = Detail.formatCarat(carat);
      if (fromDetail) return fromDetail;
    }
    var n = Number(carat);
    return Number.isFinite(n) ? n.toFixed(2) : "";
  }

  function formatShape(shape) {
    if (!isMeaningful(shape)) return "";
    return toTitleCase(String(shape).replace(/[_-]+/g, " "));
  }

  function formatOrigin(origin) {
    var value = String(origin == null ? "" : origin).trim().toLowerCase();
    if (value === "lab-grown" || value === "labgrown" || value === "lab_grown") {
      return "Lab Grown";
    }
    if (value === "natural") return "Natural";
    return value ? toTitleCase(value.replace(/[_-]+/g, " ")) : "";
  }

  function formatCut(cut) {
    var Detail = detailApi();
    if (Detail && typeof Detail.formatCut === "function") {
      return Detail.formatCut(cut) || "";
    }
    if (!isMeaningful(cut)) return "";
    var code = String(cut).trim().toUpperCase();
    if (code === "FANCY") return "";
    return CUT_LABELS[code] || toTitleCase(cut);
  }

  function formatCutSegment(cut) {
    var Detail = detailApi();
    if (Detail && typeof Detail.formatCutSegment === "function") {
      return Detail.formatCutSegment(cut) || "";
    }
    var label = formatCut(cut);
    if (!label) return "";
    if (/\bcut$/i.test(label)) return label;
    return label + " Cut";
  }

  function formatColorSegment(color) {
    var Detail = detailApi();
    if (Detail && typeof Detail.formatColorSegment === "function") {
      return Detail.formatColorSegment(color) || "";
    }
    var label = isMeaningful(color) ? String(color).trim() : "";
    return label ? label + " Color" : "";
  }

  function formatClaritySegment(clarity) {
    var Detail = detailApi();
    if (Detail && typeof Detail.formatClaritySegment === "function") {
      return Detail.formatClaritySegment(clarity) || "";
    }
    var label = isMeaningful(clarity) ? String(clarity).trim() : "";
    return label ? label + " Clarity" : "";
  }

  function formatLab(lab) {
    var Detail = detailApi();
    if (Detail && typeof Detail.gradingLab === "function") {
      return Detail.gradingLab({ lab: lab }) || "";
    }
    return isMeaningful(lab) ? String(lab).trim() : "";
  }

  function snapshotOf(item) {
    return item && item.snapshot && typeof item.snapshot === "object" ? item.snapshot : item || {};
  }

  function formatIdentity(item) {
    var snap = snapshotOf(item);
    var Detail = detailApi();
    if (Detail && typeof Detail.formatStoneIdentity === "function") {
      var fromDetail = Detail.formatStoneIdentity(snap);
      if (fromDetail) return fromDetail;
    }
    var caratLabel = formatCarat(snap.carat);
    var shapeLabel = formatShape(snap.shape);
    var originLabel = formatOrigin(snap.origin);
    return [caratLabel && caratLabel + " ct", shapeLabel, originLabel, "Diamond"]
      .filter(Boolean)
      .join(" ");
  }

  function formatShortIdentity(item) {
    var snap = snapshotOf(item);
    var caratLabel = formatCarat(snap.carat);
    var shapeLabel = formatShape(snap.shape);
    return [caratLabel && caratLabel + " ct", shapeLabel].filter(Boolean).join(" ");
  }

  function formatGrading(item) {
    var snap = snapshotOf(item);
    var Detail = detailApi();
    if (Detail && typeof Detail.formatSpecLine === "function") {
      return Detail.formatSpecLine(snap) || "";
    }
    return [
      formatCutSegment(snap.cut),
      formatColorSegment(snap.color),
      formatClaritySegment(snap.clarity),
      formatLab(snap.lab),
    ]
      .filter(isMeaningful)
      .join(" \u00b7 ");
  }

  function formatPrice(item) {
    var snap = snapshotOf(item);
    var Detail = detailApi();
    if (Detail && typeof Detail.formatPriceUsd === "function") {
      return Detail.formatPriceUsd(snap.retailPriceUsd) || "";
    }
    if (snap.retailPriceUsd == null || snap.retailPriceUsd === "") return "";
    var n = Number(snap.retailPriceUsd);
    if (!Number.isFinite(n)) return "";
    return "$" + Math.round(n).toLocaleString("en-US");
  }

  function triggerLabel(count) {
    var n = Number(count) || 0;
    if (n <= 0) return "Saved items, no saved items";
    if (n === 1) return "Saved items, 1 saved item";
    return "Saved items, " + n + " saved items";
  }

  function formatRemoveLabel(item) {
    var shortId = formatShortIdentity(item) || "diamond";
    return "Remove " + shortId + " diamond from saved items";
  }

  function bareUuid(id) {
    var IvanovAPI = global.IvanovAPI;
    if (IvanovAPI && typeof IvanovAPI.normalizeDiamondId === "function") {
      try {
        return IvanovAPI.normalizeDiamondId(id).bareUuid || "";
      } catch (err) {
        // Fall through.
      }
    }
    var Saved = savedApi();
    if (Saved && typeof Saved.normalizeId === "function") {
      return String(Saved.normalizeId(id) || "").replace(/^DIAMOND\//i, "");
    }
    return String(id == null ? "" : id).replace(/^DIAMOND\//i, "").trim();
  }

  function detailUrl(id) {
    var Detail = detailApi();
    if (Detail && typeof Detail.buildUrl === "function") {
      try {
        return Detail.buildUrl(id);
      } catch (err) {
        // Fall through to the local helper.
      }
    }
    var bare = bareUuid(id);
    return bare ? DETAIL_PATH + "?id=" + encodeURIComponent(bare) : "";
  }

  function previewItems(items, limit) {
    var list = Array.isArray(items) ? items.slice() : [];
    var cap = typeof limit === "number" && limit > 0 ? limit : PREVIEW_LIMIT;
    return list.slice(-cap).reverse();
  }

  function currentItems() {
    var Saved = savedApi();
    if (!Saved || typeof Saved.list !== "function") return [];
    try {
      return Saved.list() || [];
    } catch (err) {
      return [];
    }
  }

  function currentCount() {
    var Saved = savedApi();
    if (Saved && typeof Saved.count === "function") {
      try {
        return Saved.count();
      } catch (err) {
        // Fall through.
      }
    }
    return currentItems().length;
  }

  function renderEmptyHtml() {
    return (
      '<p class="saved-diamonds-header__empty">You haven\'t saved any diamonds yet.</p>' +
      '<a class="saved-diamonds-header__explore" href="' +
      SEARCH_PATH +
      '">EXPLORE DIAMONDS</a>'
    );
  }

  function renderCardHtml(item) {
    var snap = snapshotOf(item);
    var identity = formatIdentity(item);
    var shortId = formatShortIdentity(item);
    var grading = formatGrading(item);
    var price = formatPrice(item);
    var href = detailUrl(item && item.id);
    var image = snap.image ? String(snap.image) : "";
    var alt = (shortId || identity || "Diamond") + (/\bdiamond$/i.test(shortId || identity || "") ? "" : " diamond");
    var media =
      (image
        ? '<img data-saved-image src="' +
          escapeHtml(image) +
          '" alt="' +
          escapeHtml(alt) +
          '" width="64" height="64" loading="lazy" decoding="async">'
        : "") +
      '<span class="saved-diamonds-header__fallback" data-saved-fallback' +
      (image ? " hidden" : "") +
      ">" +
      FALLBACK_SVG +
      "</span>";
    return (
      '<article class="saved-diamonds-header__item">' +
      '<a class="saved-diamonds-header__card" href="' +
      escapeHtml(href) +
      '">' +
      '<span class="saved-diamonds-header__media">' +
      media +
      "</span>" +
      '<span class="saved-diamonds-header__copy">' +
      '<span class="saved-diamonds-header__identity">' +
      escapeHtml(identity) +
      "</span>" +
      (grading
        ? '<span class="saved-diamonds-header__grading">' + escapeHtml(grading) + "</span>"
        : "") +
      (price
        ? '<span class="saved-diamonds-header__price">' + escapeHtml(price) + "</span>"
        : "") +
      "</span>" +
      "</a>" +
      '<button type="button" class="saved-diamonds-header__remove" data-saved-remove data-diamond-id="' +
      escapeHtml(item && item.id ? String(item.id) : "") +
      '" aria-label="' +
      escapeHtml(formatRemoveLabel(item)) +
      '">\u00d7</button>' +
      "</article>"
    );
  }

  function renderFooterHtml(count) {
    var n = Number(count) || 0;
    var label = "VIEW SAVED ITEMS" + (n > 0 ? " (" + n + ")" : "");
    return (
      '<a class="saved-diamonds-header__footer" data-saved-footer href="' +
      PAGE_PATH +
      '">' +
      escapeHtml(label) +
      "</a>"
    );
  }

  function renderPanelHtml(items) {
    var list = Array.isArray(items) ? items : [];
    var preview = previewItems(list, PREVIEW_LIMIT);
    if (!preview.length) return renderEmptyHtml();
    return (
      '<p class="saved-diamonds-header__category">DIAMONDS</p>' +
      '<ul class="saved-diamonds-header__list">' +
      preview
        .map(function (item) {
          return "<li>" + renderCardHtml(item) + "</li>";
        })
        .join("") +
      "</ul>" +
      renderFooterHtml(list.length)
    );
  }

  function matchesMedia(query) {
    if (!global.matchMedia) return false;
    try {
      var mq = global.matchMedia(query);
      return !!(mq && mq.matches);
    } catch (err) {
      return false;
    }
  }

  function isDesktop() {
    return matchesMedia(DESKTOP_MEDIA);
  }

  function closest(node, selector) {
    if (!node) return null;
    if (typeof node.closest === "function") return node.closest(selector);
    return null;
  }

  function bindImageFallbacks(root) {
    if (!root || typeof root.querySelectorAll !== "function") return;
    var images = root.querySelectorAll("[data-saved-image]");
    var list =
      images && typeof images.forEach === "function"
        ? images
        : Array.prototype.slice.call(images || []);
    list.forEach(function (img) {
      if (!img || img.__ivanovSavedFallbackBound) return;
      img.__ivanovSavedFallbackBound = true;
      if (typeof img.addEventListener !== "function") return;
      img.addEventListener("error", function () {
        img.hidden = true;
        var parent = img.parentNode;
        var fallback =
          parent && typeof parent.querySelector === "function"
            ? parent.querySelector("[data-saved-fallback]")
            : null;
        if (fallback) fallback.hidden = false;
      });
    });
  }

  function setHidden(node, hidden) {
    if (!node) return;
    if (hidden) {
      node.hidden = true;
      if (typeof node.setAttribute === "function") node.setAttribute("hidden", "");
    } else {
      node.hidden = false;
      if (typeof node.removeAttribute === "function") node.removeAttribute("hidden");
    }
  }

  function createController(root, options) {
    options = options || {};
    if (!root || root.__ivanovSavedHeaderBound) return root && root.__ivanovSavedHeaderController;
    var doc = options.document || global.document;
    var win = options.window || global;
    var trigger = root.querySelector("[data-saved-trigger]");
    var panel = root.querySelector("[data-saved-panel]");
    var body = root.querySelector("[data-saved-body]");
    var overlay = root.querySelector("[data-saved-overlay]");
    var closeBtn = root.querySelector("[data-saved-close]");
    var countEl = trigger && trigger.querySelector("[data-saved-count]");
    var openState = false;
    var openReason = "";
    var lastTrigger = trigger;
    var renderedKey = "";

    function lockPage(lock) {
      if (!doc || !doc.body) return;
      doc.body.style.overflow = lock && !isDesktop() ? "hidden" : "";
    }

    function firstPanelFocusable() {
      if (!panel) return null;
      if (closeBtn) return closeBtn;
      if (typeof panel.querySelector === "function") {
        return panel.querySelector("a, button, [href], [tabindex]:not([tabindex='-1'])");
      }
      return panel;
    }

    function refresh(force) {
      var items = currentItems();
      var count = currentCount();
      var html = renderPanelHtml(items);
      var key = count + ":" + html;
      if (trigger) {
        if (typeof trigger.setAttribute === "function") {
          trigger.setAttribute("aria-label", triggerLabel(count));
          trigger.setAttribute("aria-expanded", openState ? "true" : "false");
        }
        if (trigger.classList) {
          if (count > 0) trigger.classList.add("is-active");
          else trigger.classList.remove("is-active");
        }
      }
      if (countEl) {
        countEl.textContent = count > 0 ? String(count) : "";
        setHidden(countEl, count <= 0);
      }
      if (body && (force || key !== renderedKey)) {
        body.innerHTML = html;
        renderedKey = key;
        bindImageFallbacks(body);
      }
      if (root.classList) {
        if (openState) root.classList.add("is-open");
        else root.classList.remove("is-open");
        if (openState && !isDesktop()) root.classList.add("is-drawer");
        else root.classList.remove("is-drawer");
      }
      return { count: count, items: items };
    }

    function close(options) {
      if (!openState) return;
      // History/BFCache return must not focus the Saved trigger — that can
      // fight the browser's own scroll restoration during Browser Back.
      var historyReturn = !!(options && options.historyReturn === true);
      openState = false;
      openReason = "";
      if (panel) {
        setHidden(panel, true);
        if (typeof panel.setAttribute === "function") {
          panel.setAttribute("aria-modal", "false");
        }
      }
      setHidden(overlay, true);
      if (trigger && typeof trigger.setAttribute === "function") {
        trigger.setAttribute("aria-expanded", "false");
      }
      if (root.classList) {
        root.classList.remove("is-open");
        root.classList.remove("is-drawer");
      }
      lockPage(false);
      if (historyReturn) return;
      if (lastTrigger && typeof lastTrigger.focus === "function") {
        try {
          lastTrigger.focus({ preventScroll: true });
        } catch (err) {
          lastTrigger.focus();
        }
      }
    }

    function open(reason) {
      openState = true;
      openReason = reason || "click";
      lastTrigger = trigger;
      refresh(true);
      if (panel) {
        setHidden(panel, false);
        if (typeof panel.setAttribute === "function") {
          panel.setAttribute("aria-modal", isDesktop() ? "false" : "true");
        }
      }
      setHidden(overlay, isDesktop());
      if (trigger && typeof trigger.setAttribute === "function") {
        trigger.setAttribute("aria-expanded", "true");
      }
      if (root.classList) {
        root.classList.add("is-open");
        if (!isDesktop()) root.classList.add("is-drawer");
      }
      lockPage(true);
      if (openReason === "keyboard" || !isDesktop()) {
        var focusTarget = firstPanelFocusable();
        if (focusTarget && typeof focusTarget.focus === "function") {
          try {
            focusTarget.focus({ preventScroll: true });
          } catch (err) {
            focusTarget.focus();
          }
        }
      }
    }

    function toggle(reason) {
      if (openState) close();
      else open(reason || "click");
    }

    function onTriggerClick(event) {
      if (event && typeof event.preventDefault === "function") event.preventDefault();
      toggle("click");
    }

    function onTriggerKeydown(event) {
      if (!event) return;
      if (event.key === "Enter" || event.key === " ") {
        if (typeof event.preventDefault === "function") event.preventDefault();
        if (openState) close();
        else open("keyboard");
      }
    }

    function onCloseClick(event) {
      if (event && typeof event.preventDefault === "function") event.preventDefault();
      close();
    }

    function onDocumentClick(event) {
      if (!openState) return;
      var target = event && event.target;
      if (root.contains && target && root.contains(target)) return;
      close();
    }

    function onDocumentKeydown(event) {
      if (!openState || !event) return;
      if (event.key === "Escape" || event.key === "Esc") {
        if (typeof event.preventDefault === "function") event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab" || isDesktop() || !panel) return;
      var focusable = [];
      if (typeof panel.querySelectorAll === "function") {
        focusable = Array.prototype.slice.call(
          panel.querySelectorAll("a, button, [href], [tabindex]:not([tabindex='-1'])")
        ).filter(function (node) {
          return node && !node.hidden && node.getAttribute && node.getAttribute("hidden") == null;
        });
      }
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = doc && doc.activeElement;
      if (event.shiftKey && active === first) {
        if (typeof event.preventDefault === "function") event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        if (typeof event.preventDefault === "function") event.preventDefault();
        first.focus();
      }
    }

    function onRootClick(event) {
      var target = event && event.target;
      var removeBtn = closest(target, "[data-saved-remove]");
      if (!removeBtn) return;
      if (event && typeof event.preventDefault === "function") event.preventDefault();
      if (event && typeof event.stopPropagation === "function") event.stopPropagation();
      var Saved = savedApi();
      var id = removeBtn.getAttribute ? removeBtn.getAttribute("data-diamond-id") : "";
      if (Saved && typeof Saved.remove === "function" && id) Saved.remove(id);
    }

    function onChange() {
      var before = currentCount();
      refresh(true);
      if (openState && before === 0 && currentCount() === 0) {
        // Keep empty panel open if the user already opened it.
      }
    }

    function onResize() {
      if (!openState) return;
      refresh(false);
      if (panel && typeof panel.setAttribute === "function") {
        panel.setAttribute("aria-modal", isDesktop() ? "false" : "true");
      }
      setHidden(overlay, isDesktop());
      if (root.classList) {
        if (isDesktop()) root.classList.remove("is-drawer");
        else root.classList.add("is-drawer");
      }
      lockPage(!isDesktop());
    }

    // Close before BFCache freeze so Browser Back cannot restore an open drawer.
    function onPageHide() {
      if (!openState) return;
      close({ historyReturn: true });
    }

    // Defensive: if a frozen open state survives, clear it on BFCache restore.
    function onPageShow(event) {
      if (!event || event.persisted !== true) return;
      if (!openState) return;
      close({ historyReturn: true });
    }

    if (typeof trigger.addEventListener === "function") {
      trigger.addEventListener("click", onTriggerClick);
      trigger.addEventListener("keydown", onTriggerKeydown);
    }
    if (closeBtn && typeof closeBtn.addEventListener === "function") {
      closeBtn.addEventListener("click", onCloseClick);
    }
    if (overlay && typeof overlay.addEventListener === "function") {
      overlay.addEventListener("click", onCloseClick);
    }
    if (typeof root.addEventListener === "function") {
      root.addEventListener("click", onRootClick);
    }
    if (win && typeof win.addEventListener === "function") {
      win.addEventListener(CHANGE_EVENT, onChange);
      win.addEventListener("resize", onResize);
      win.addEventListener("pagehide", onPageHide);
      win.addEventListener("pageshow", onPageShow);
    }
    if (doc && typeof doc.addEventListener === "function") {
      doc.addEventListener("click", onDocumentClick);
      doc.addEventListener("keydown", onDocumentKeydown);
    }

    refresh(true);

    var controller = {
      open: open,
      close: close,
      toggle: toggle,
      refresh: refresh,
      isOpen: function () {
        return openState;
      },
      destroy: function () {
        lockPage(false);
        if (win && typeof win.removeEventListener === "function") {
          win.removeEventListener(CHANGE_EVENT, onChange);
          win.removeEventListener("resize", onResize);
          win.removeEventListener("pagehide", onPageHide);
          win.removeEventListener("pageshow", onPageShow);
        }
        if (doc && typeof doc.removeEventListener === "function") {
          doc.removeEventListener("click", onDocumentClick);
          doc.removeEventListener("keydown", onDocumentKeydown);
        }
      },
    };
    root.__ivanovSavedHeaderBound = true;
    root.__ivanovSavedHeaderController = controller;
    return controller;
  }

  function boot(options) {
    options = options || {};
    var doc = options.document || global.document;
    if (!doc || typeof doc.querySelectorAll !== "function") return [];
    var nodes = doc.querySelectorAll("saved-diamonds-header, [data-saved-header]");
    var list =
      nodes && typeof nodes.forEach === "function"
        ? nodes
        : Array.prototype.slice.call(nodes || []);
    var controllers = [];
    list.forEach(function (node) {
      controllers.push(createController(node, options));
    });
    return controllers;
  }

  // Unknown <saved-diamonds-header> markup is enough; do not register a
  // custom element class here. Sahara already owns drawer custom elements.

  function start() {
    boot();
  }

  if (global.document && global.document.readyState === "loading") {
    global.document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }

  global.DiamondHeaderSaved = {
    PREVIEW_LIMIT: PREVIEW_LIMIT,
    SEARCH_PATH: SEARCH_PATH,
    DETAIL_PATH: DETAIL_PATH,
    PAGE_PATH: PAGE_PATH,
    DESKTOP_MEDIA: DESKTOP_MEDIA,
    CHANGE_EVENT: CHANGE_EVENT,
    FOOTER_CTA: true,
    renderFooterHtml: renderFooterHtml,
    formatIdentity: formatIdentity,
    formatShortIdentity: formatShortIdentity,
    formatGrading: formatGrading,
    formatPrice: formatPrice,
    formatRemoveLabel: formatRemoveLabel,
    triggerLabel: triggerLabel,
    detailUrl: detailUrl,
    previewItems: previewItems,
    renderPanelHtml: renderPanelHtml,
    renderCardHtml: renderCardHtml,
    createController: createController,
    boot: boot,
  };
})(typeof window !== "undefined" ? window : this);
